// Drift → Affärssystem: the log of reports to the ERP (owner 2026-11-02: the rows under
// "Rapportering till affärssystem" will grow enormously once the write-back is in use — so the
// log leaves the settings page; mockup admin-v1/erp-reports). Counters per state, filters, rows
// 50 at a time from the server, a detail page per report with the ERP's answer, the call and the
// general payload. "Skicka igen" is the retry the settings page had.

import { useCallback, useEffect, useState } from "react";
import { Admin, type ErpReportKind, type ErpReportOut, type ErpReportState, type ErpReportSummary } from "../api/client.js";
import { t } from "../i18n.js";
import { DetailBar, useDetailHistory } from "./settings-ui.jsx";
import { ErpJournal } from "./ErpJournal.jsx";

const PAGE = 50;
const STATES: ErpReportState[] = ["pending", "claimed", "shadow", "sent", "failed", "skipped"];
const KINDS: ErpReportKind[] = ["sales-order.picked", "sales-order.released", "receipt.received", "receipt.closed", "stock.adjusted", "stock.moved"];
const fmt = (iso: string) => new Date(iso).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" });
const stateLabel = (s: ErpReportState) => t(`adm.set.erpReport.st.${s}` as "adm.set.erpReport.st.pending");
const kindLabel = (k: ErpReportKind) => t(`adm.set.erpReport.kind.${k}` as "adm.set.erpReport.kind.sales-order.picked");
const pillClass = (s: ErpReportState) => (s === "sent" ? "ok" : s === "failed" ? "bad" : s === "shadow" ? "warn" : "mut");
export const refLabel = (r: ErpReportOut) => (r.kind === "stock.adjusted" ? ((r.payload as { roundName?: string } | undefined)?.roundName ?? r.ref.slice(0, 8)) : r.ref);
/** One line about the outcome, for the list. */
const resultLine = (r: ErpReportOut): string => {
  if (r.result?.error) return r.result.error.slice(0, 160);
  const parts: string[] = [];
  const erp = r.result?.erp as { status?: string } | undefined;
  if (erp?.status) parts.push(`${t("adm.erpReports.erpSaid")} ${erp.status}`);
  if (r.result?.operations) parts.push(t("adm.erpReports.ops", { n: r.result.operations }));
  if (r.result?.notes?.length) parts.push(r.result.notes[0]!.slice(0, 90));
  return parts.join(" · ");
};

type Period = "30" | "90" | "all";
const since = (p: Period): string | undefined => (p === "all" ? undefined : new Date(Date.now() - Number(p) * 86400_000).toISOString());

/** System → Affärssystemet (owner 2026-11-04: under System, since ordinary users do not look here daily): two tabs,
 *  the report log and the ERP's journal. */
export function ErpReports() {
  const [tab, setTab] = useState<"reports" | "journal">(() => (location.hash.includes("journal") ? "journal" : "reports"));
  const [summary, setSummary] = useState<ErpReportSummary | null>(null);
  const [state, setState] = useState<ErpReportState | "all">("all");
  const [kind, setKind] = useState<ErpReportKind | "all">("all");
  const [period, setPeriod] = useState<Period>("30");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState<ErpReportOut[]>([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState<ErpReportOut | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useDetailHistory(!!open, () => setOpen(null));

  const load = useCallback(async (offset: number) => {
    setErr(null);
    try {
      const r = await Admin.erpReports({ ...(state !== "all" ? { state } : {}), ...(kind !== "all" ? { kind } : {}), ...(q.trim() ? { q: q.trim() } : {}), ...(since(period) ? { since: since(period)! } : {}), limit: PAGE, offset });
      setRows((prev) => (offset === 0 ? r.items : [...prev, ...r.items]));
      setTotal(r.total);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }, [state, kind, q, period]);
  const reload = useCallback(() => { void load(0); Admin.erpReportSummary().then(setSummary).catch(() => undefined); }, [load]);
  useEffect(() => { const h = setTimeout(reload, q ? 250 : 0); return () => clearTimeout(h); }, [reload, q]);

  const release = async () => {
    setMsg(null);
    try { const r = await Admin.releaseErpReports(); setMsg(t("adm.erpReports.released", { until: fmt(r.until) })); reload(); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const dismiss = async (r: ErpReportOut) => {
    try { await Admin.dismissErpReport(r.id); setMsg(t("adm.erpReports.dismissed", { ref: refLabel(r) })); reload(); if (open) setOpen(await Admin.erpReport(r.id)); } catch (e) { setMsg(e instanceof Error ? e.message : String(e)); }
  };
  const retry = async (r: ErpReportOut) => {
    setMsg(null);
    try { await Admin.retryErpReport(r.id); setMsg(t("adm.erpReports.retried", { ref: refLabel(r) })); reload(); if (open) setOpen(await Admin.erpReport(r.id)); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  };
  const show = async (r: ErpReportOut) => { try { setOpen(await Admin.erpReport(r.id)); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } };

  return (
    <>
      <div className="adm-top">
        <div><h1>{t("adm.erpReports.title")}</h1><div className="crumb">{t("adm.erpReports.sub")}</div></div>
        {!open && tab === "reports" && <div className="right"><input className="ain" style={{ height: 34, width: 260 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("adm.erpReports.search")} /></div>}
      </div>
      <div className="adm-content">
        {!open && (
          <div className="adm-tabs">
            <button type="button" className={tab === "reports" ? "on" : ""} onClick={() => setTab("reports")}>{t("adm.erp.tab.reports")}</button>
            <button type="button" className={tab === "journal" ? "on" : ""} onClick={() => setTab("journal")}>{t("adm.erp.tab.journal")}</button>
          </div>
        )}
        {tab === "journal" && !open && <ErpJournal />}
        {tab === "journal" && !open ? null : <>
        {err && <p className="ahint" style={{ color: "var(--bad)" }}>{err}</p>}
        {msg && <p className="ahint" style={{ color: "var(--ok)" }}>{msg}</p>}
        {/* The write guard (owner's rule 2026-11-02): a flood of pending reports waits for a person. */}
        {summary?.guard.held && !open && (
          <div className="card" style={{ borderColor: "var(--bad)" }}><div className="bd" style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <span><b>{t("adm.erpReports.held", { n: summary.guard.pending, max: summary.guard.maxPerRun })}</b> {t("adm.erpReports.held.msg")}</span>
            <button type="button" className="abtn sm primary" onClick={() => void release()}>{t("adm.erpReports.release")}</button>
          </div></div>
        )}
        {summary?.guard.releasedUntil && !open && <p className="ahint">{t("adm.erpReports.releasedUntil", { until: fmt(summary.guard.releasedUntil) })}</p>}
        <div className={`adm-cols${open ? " detail-open" : ""}`}>
          {!open && (
            <div className="adm-grow">
              <div className="card">
                {summary && (
                  <div className="bd" style={{ display: "flex", gap: 22, flexWrap: "wrap", paddingBottom: 4 }}>
                    {STATES.filter((s) => s !== "claimed").map((s) => (
                      <button key={s} type="button" className="navlink" style={{ background: "none", border: 0, padding: 0, textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit" }} onClick={() => setState(state === s ? "all" : s)}>
                        <div style={{ fontSize: 22, fontWeight: 700, color: s === "failed" && summary.counts[s] > 0 ? "var(--bad)" : undefined }}>{(summary.counts[s] + (s === "pending" ? summary.counts.claimed : 0)).toLocaleString("sv-SE")}</div>
                        <div className="mut" style={{ fontSize: 12 }}>{stateLabel(s)}</div>
                      </button>
                    ))}
                  </div>
                )}
                <div className="bd" style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderTop: "1px solid var(--line)" }}>
                  <div className="achips">
                    <button type="button" className={`achip${state === "all" ? " on" : ""}`} onClick={() => setState("all")}>{t("adm.erpReports.all")}</button>
                    {STATES.filter((s) => s !== "claimed").map((s) => <button key={s} type="button" className={`achip${state === s ? " on" : ""}`} onClick={() => setState(s)}>{stateLabel(s)}</button>)}
                  </div>
                  <select value={kind} onChange={(e) => setKind(e.target.value as ErpReportKind | "all")}>
                    <option value="all">{t("adm.erpReports.allKinds")}</option>
                    {KINDS.map((k) => <option key={k} value={k}>{kindLabel(k)}</option>)}
                  </select>
                  <select value={period} onChange={(e) => setPeriod(e.target.value as Period)}>
                    <option value="30">{t("adm.erpReports.period.30")}</option>
                    <option value="90">{t("adm.erpReports.period.90")}</option>
                    <option value="all">{t("adm.erpReports.period.all")}</option>
                  </select>
                </div>
                <div className="bd flush" style={{ borderTop: "1px solid var(--line)" }}>
                  <table className="at">
                    <thead><tr><th>{t("adm.col.time")}</th><th>{t("adm.set.erpReport.col.kind")}</th><th>{t("adm.set.erpReport.col.ref")}</th><th>{t("adm.col.status")}</th><th>{t("adm.set.erpReport.col.result")}</th><th>{t("adm.erpReports.col.agent")}</th></tr></thead>
                    <tbody>
                      {rows.length === 0 && <tr><td colSpan={6} className="mut">{t("adm.set.erpReport.none")}</td></tr>}
                      {rows.map((r) => (
                        <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => void show(r)}>
                          <td className="id">{fmt(r.createdAt)}</td>
                          <td>{kindLabel(r.kind)}</td>
                          <td className="id">{refLabel(r)}</td>
                          <td><span className={`pill ${pillClass(r.state)}`}>{stateLabel(r.state)}</span>{r.attempts > 1 && <div className="mut" style={{ fontSize: 12 }}>{t("adm.set.erpReport.attempts", { n: r.attempts })}</div>}</td>
                          <td style={{ maxWidth: 480, color: r.result?.error ? "var(--bad)" : undefined }}>{resultLine(r)}</td>
                          <td className="id">{r.claimedBy ?? "—"}</td>
                        </tr>
                      ))}
                      {rows.length < total && (
                        <tr><td colSpan={6} style={{ textAlign: "center" }}>
                          <button type="button" className="abtn sm" onClick={() => void load(rows.length)}>{t("adm.erpReports.more", { n: Math.min(PAGE, total - rows.length) })}</button>
                          <span className="mut" style={{ marginLeft: 10, fontSize: 12 }}>{t("adm.erpReports.of", { a: rows.length, b: total })}</span>
                        </td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          {open && <ReportDetail r={open} onBack={() => setOpen(null)} onRetry={() => void retry(open)} onDismiss={() => void dismiss(open)} />}
        </div>
        </>}
      </div>
    </>
  );
}

function ReportDetail({ r, onBack, onRetry, onDismiss }: { r: ErpReportOut; onBack: () => void; onRetry: () => void; onDismiss: () => void }) {
  const erp = (r.result?.erp ?? null) as Record<string, unknown> | null;
  const retryable = r.state === "failed" || r.state === "shadow" || r.state === "skipped" || r.state === "sent";
  // Put aside without sending (owner 2026-11-02): a failed or waiting report that is overtaken.
  const dismissable = r.state === "failed" || r.state === "pending" || r.state === "shadow";
  const [showPayload, setShowPayload] = useState(false);
  return (
    <div className="card adm-grow adm-detail">
      <DetailBar title={`${kindLabel(r.kind)} · ${refLabel(r)}`} sub={<><span className={`pill ${pillClass(r.state)}`}>{stateLabel(r.state)}</span> {r.claimedAt ? `${fmt(r.claimedAt)}${r.claimedBy ? ` · ${r.claimedBy}` : ""}` : ""} · {t("adm.set.erpReport.attempts", { n: r.attempts })}</>} onBack={onBack}
        right={retryable || dismissable ? <span style={{ display: "flex", gap: 6 }}>{dismissable && <button type="button" className="abtn sm" onClick={onDismiss}>{t("adm.erpReports.dismiss")}</button>}{retryable && <button type="button" className="abtn sm" onClick={onRetry}>{t("adm.set.erpReport.retry")}</button>}</span> : undefined} />
      <div className="bd aform" style={{ gridTemplateColumns: "180px 1fr" }}>
        <span>{t("adm.erpReports.created")}</span><div>{fmt(r.createdAt)}{r.createdBy ? ` · ${r.createdBy.replace(/^user:/, "")}` : ""}</div>
        {r.result?.mode && <><span>{t("adm.set.erpReport.mode")}</span><div>{r.result.mode === "live" ? t("adm.set.erpReport.mode.live") : t("adm.set.erpReport.mode.shadow")}</div></>}
        <span>{t("adm.erpReports.answer")}</span>
        <div>
          {r.result?.error && <div style={{ color: "var(--bad)", whiteSpace: "pre-wrap" }}>{r.result.error}</div>}
          {erp && Object.entries(erp).map(([k, v]) => <div key={k} style={{ fontSize: 13 }}><span className="mut">{k}</span> {Array.isArray(v) ? (v.length ? v.map(String).join(", ") : "—") : typeof v === "object" && v ? JSON.stringify(v) : String(v)}</div>)}
          {!r.result?.error && !erp && <span className="mut">—</span>}
        </div>
        {!!r.result?.notes?.length && <><span>{t("adm.erpReports.notes")}</span><div>{r.result.notes.map((n, i) => <div key={i} style={{ fontSize: 13 }}>{n}</div>)}</div></>}
      </div>
      {r.result?.preview && (
        <div className="bd" style={{ borderTop: "1px solid var(--line)" }}>
          <b>{t("adm.erpReports.call")}</b> <span className="mut" style={{ fontSize: 12 }}>{t("adm.erpReports.ops", { n: r.result.operations ?? 0 })}</span>
          <pre style={{ fontSize: 11, whiteSpace: "pre-wrap", maxHeight: 420, overflow: "auto", margin: "6px 0 0" }}>{r.result.preview}</pre>
        </div>
      )}
      <div className="bd" style={{ borderTop: "1px solid var(--line)" }}>
        <b>{t("adm.erpReports.payload")}</b> <span className="mut" style={{ fontSize: 12 }}>{t("adm.erpReports.payload.sub")}</span>{" "}
        <button type="button" className="abtn sm" onClick={() => setShowPayload(!showPayload)}>{showPayload ? t("common.hide") : t("common.show")}</button>
        {showPayload && <pre style={{ fontSize: 11, whiteSpace: "pre-wrap", maxHeight: 420, overflow: "auto", margin: "6px 0 0" }}>{JSON.stringify(r.payload, null, 2)}</pre>}
      </div>
    </div>
  );
}
